import { useEffect, useRef, useState } from 'react';

import { useLocalSearchParams } from 'expo-router';

import { groupAndSortTransactionsByMonth } from './utils';

const HIGHLIGHT_MS = 2500;

const firstParam = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

export const useHistoryTransactionHighlight = <
  T extends { timeStamp: string; transactionId?: string }
>(
  tableData: T[],
  isTableReady = true
) => {
  const { transactionId: transactionIdParam } = useLocalSearchParams<{
    transactionId?: string | string[];
  }>();
  const transactionId = firstParam(transactionIdParam);
  const handledTransactionIdRef = useRef<string | undefined>(undefined);
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | undefined>();
  const [minVisibleMonths, setMinVisibleMonths] = useState(0);

  useEffect(() => {
    if (!transactionId || handledTransactionIdRef.current === transactionId) {
      return;
    }

    const monthIndex = groupAndSortTransactionsByMonth(tableData).findIndex(([, rows]) =>
      rows.some(row => row.transactionId === transactionId)
    );
    if (monthIndex < 0 || !isTableReady) {
      return;
    }

    handledTransactionIdRef.current = transactionId;
    setMinVisibleMonths(monthIndex + 1);
    setHighlightedTransactionId(transactionId);
  }, [isTableReady, tableData, transactionId]);

  useEffect(() => {
    if (!highlightedTransactionId) {
      return;
    }

    const timeoutId = setTimeout(() => setHighlightedTransactionId(undefined), HIGHLIGHT_MS);
    return () => clearTimeout(timeoutId);
  }, [highlightedTransactionId]);

  return { highlightedTransactionId, minVisibleMonths };
};
